import { Typography } from '@mui/material'
import React, { Fragment, memo, useCallback, useState } from 'react'
import { useDispatch } from 'react-redux'
import tw from 'twin.macro'
import { InfoContainer } from 'src/components/shared/InfoContainer'
import { FileUploadInput } from 'src/components/shared/FileUploadInput'
import { SelfieCaptureInput } from 'src/components/shared/SelfieCaptureInput'
import { UploadedFile } from 'src/components/shared/UploadedFile'
import { Button } from 'src/components/shared/Button'
import { FormErrorMessage } from 'src/components/shared/form/FormErrorMessage'
import { SubmitTemporaryDriverLicense } from 'src/constants/actionTypes'
import { MAX_FILE_SIZE_BYTES } from 'src/constants/fileUpload'
import { createAsyncAction } from 'src/utils/reduxUtils'

interface Props {
  stepId: string
}

const DOCUMENT_ACCEPT_TYPES = ['image/png', 'image/jpeg', 'application/pdf']

const TemporaryDriverLicenseFormComponent = ({ stepId }: Props) => {
  const dispatch = useDispatch()
  const [temporaryLicenseFile, setTemporaryLicenseFile] = useState<File | null>(null)
  const [selfieFile, setSelfieFile] = useState<File | null>(null)
  const [previousLicenseFile, setPreviousLicenseFile] = useState<File | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const canSubmit = Boolean(temporaryLicenseFile) && Boolean(selfieFile) && !isSubmitting

  const handleSubmit = useCallback(async () => {
    if (!temporaryLicenseFile || !selfieFile) {
      return
    }

    setIsSubmitting(true)
    setError(null)

    try {
      await createAsyncAction(
        dispatch,
        SubmitTemporaryDriverLicense.request({
          stepId,
          temporaryLicenseFile,
          selfieFile,
          ...(previousLicenseFile && { previousLicenseFile }),
        }),
      )
    } catch (err: any) {
      setError(err)
    } finally {
      setIsSubmitting(false)
    }
  }, [dispatch, stepId, temporaryLicenseFile, selfieFile, previousLicenseFile])

  return (
    <Fragment>
      <Typography css={tw`text-shades-400 mt-6`} variant="overline">
        Upload front of the temporary/paper license
      </Typography>
      <div css={tw`bg-shades-0 p-4 rounded-2xl mt-2`}>
        <InfoContainer
          dataTestId="contractors-onboarding-temporary-driver-license-notice-info"
          useInfoIcon
        >
          Temporary licenses can't be verified automatically. Upload a clear photo or PDF of your
          temporary license and our team will review it manually.
        </InfoContainer>
        <div css={tw`mt-4`}>
          {temporaryLicenseFile ? (
            <UploadedFile
              file={temporaryLicenseFile}
              onRemove={() => setTemporaryLicenseFile(null)}
              containerDataTestId="contractors-onboarding-temporary-driver-license-file-uploaded"
              removeButtonTestId="contractors-onboarding-temporary-driver-license-file-remove-btn"
            />
          ) : (
            <FileUploadInput
              acceptTypes={DOCUMENT_ACCEPT_TYPES}
              fileSizeLimit={MAX_FILE_SIZE_BYTES}
              onSelect={setTemporaryLicenseFile}
              inputDataTestId="contractors-onboarding-temporary-driver-license-file-input"
            />
          )}
        </div>
      </div>
      {temporaryLicenseFile && (
        <Fragment>
          <Typography css={tw`text-shades-400 mt-6`} variant="overline">
            Take a Selfie
          </Typography>
          <div css={tw`bg-shades-0 p-4 rounded-2xl mt-2`}>
            <InfoContainer
              dataTestId="contractors-onboarding-temporary-driver-license-selfie-info"
              useInfoIcon
            >
              Take a clear photo of your face so we can match it to your document
            </InfoContainer>
            <div css={tw`mt-4`}>
              {selfieFile ? (
                <UploadedFile
                  file={selfieFile}
                  onRemove={() => setSelfieFile(null)}
                  containerDataTestId="contractors-onboarding-temporary-driver-license-selfie-uploaded"
                  removeButtonTestId="contractors-onboarding-temporary-driver-license-selfie-remove-btn"
                />
              ) : (
                <SelfieCaptureInput
                  fileSizeLimit={MAX_FILE_SIZE_BYTES}
                  onSelect={setSelfieFile}
                  inputDataTestId="contractors-onboarding-temporary-driver-license-selfie-input"
                />
              )}
            </div>
          </div>
        </Fragment>
      )}
      {selfieFile && (
        <Fragment>
          <Typography css={tw`text-shades-400 mt-6`} variant="overline">
            Previous Driver License (Optional)
          </Typography>
          <div css={tw`bg-shades-0 p-4 rounded-2xl mt-2`}>
            <InfoContainer
              dataTestId="contractors-onboarding-temporary-driver-license-previous-info"
              useInfoIcon
            >
              Upload your previous license to help speed up the review.
            </InfoContainer>
            <div css={tw`mt-4`}>
              {previousLicenseFile ? (
                <UploadedFile
                  file={previousLicenseFile}
                  onRemove={() => setPreviousLicenseFile(null)}
                  containerDataTestId="contractors-onboarding-temporary-driver-license-previous-file-uploaded"
                  removeButtonTestId="contractors-onboarding-temporary-driver-license-previous-file-remove-btn"
                />
              ) : (
                <FileUploadInput
                  acceptTypes={DOCUMENT_ACCEPT_TYPES}
                  fileSizeLimit={MAX_FILE_SIZE_BYTES}
                  onSelect={setPreviousLicenseFile}
                  inputDataTestId="contractors-onboarding-temporary-driver-license-previous-file-input"
                />
              )}
            </div>
          </div>
        </Fragment>
      )}
      <FormErrorMessage css={tw`mt-4`} error={error} />
      <Button
        className={tw`w-full mt-6`}
        loading={isSubmitting}
        disabled={!canSubmit}
        onClick={handleSubmit}
        data-testid="contractors-onboarding-temporary-driver-license-submit-btn"
      >
        Submit for review
      </Button>
    </Fragment>
  )
}

export const TemporaryDriverLicenseForm = memo(TemporaryDriverLicenseFormComponent)
